import {ComponentType, useState} from 'react';
import {BodySite, BodySiteSelector, TextWithIcon, Condition} from '@legit.health/ui';
import HelpIcon from '@mui/icons-material/Help';
import {Box, Typography} from '@mui/material';
import {TFunction} from 'i18next';
import {NO_CONDITION} from '@/shared/components/diagnosticReport/DiagnosticReportForm/const/noCondition';
import BottomBar from '../BottomBar';
import ConditionSelector from './components/ConditionSelector';

export type Value = {
  bodySiteId: string | null;
  conditionId: string | null;
  visitIdentifier: string;
};

export default function SelectPathologyStep({
  bodySites,
  conditions,
  visitIdentifierComponent: VisitIdentifierComponent,
  onContinue,
  t,
  value: initialValue
}: {
  bodySites: BodySite[];
  conditions: Condition[];
  visitIdentifierComponent?: ComponentType<{
    onChange: (visitIdentifier: string) => void;
    value: string;
  }>;
  onContinue: (value: Value) => void;
  t: TFunction;
  value: Value;
}) {
  const [value, setValue] = useState<Value>(initialValue);

  function handleSelectCondition(conditionId: string | null) {
    setValue((v) => ({
      ...v,
      bodySiteId: null,
      conditionId
    }));
  }

  function handleSelectBodySite(value: string | null) {
    setValue((v) => ({
      ...v,
      bodySiteId: value
    }));
  }

  function handleChangeExtraComponent(visitIdentifier: string) {
    setValue((v) => ({
      ...v,
      visitIdentifier
    }));
  }

  function handleNext() {
    onContinue(value);
  }

  return (
    <Box
      data-testid="selectPathologyContainer"
      sx={{
        maxWidth: 'md',
        mx: 'auto'
      }}
    >
      <Typography
        variant="h4"
        align="center"
        sx={{
          mb: '16px'
        }}
      >
        {t('shared/common:selectPathology')}
      </Typography>
      <ConditionSelector
        selectedConditionId={value.conditionId}
        conditions={conditions}
        onSelectCondition={handleSelectCondition}
        t={t}
      />
      {!value.conditionId && (
        <Box
          sx={{
            mt: 2,
            textAlign: 'center'
          }}
        >
          <TextWithIcon iconLeft={<HelpIcon color="disabled" />}>
            {t('shared/common:diagnosticReport.form.selectPathology.ifNoPathology')}
          </TextWithIcon>
        </Box>
      )}
      {value.conditionId === NO_CONDITION && (
        <>
          <Box
            sx={{
              mt: 2
            }}
          >
            <Typography
              variant="h4"
              align="center"
              sx={{
                mb: '16px'
              }}
            >
              {t('shared/common:diagnosticReport.form.selectBodySite')}
            </Typography>
          </Box>
          <BodySiteSelector
            id="bodySite"
            name="bodySite"
            onChange={handleSelectBodySite}
            value={value.bodySiteId}
            bodySites={bodySites}
            property="id"
            label={t('ui:diagnosticReport.bodySiteFieldLabel')}
          />
        </>
      )}
      {VisitIdentifierComponent && (
        <Box
          sx={{
            mt: 4
          }}
        >
          <VisitIdentifierComponent
            onChange={handleChangeExtraComponent}
            value={value.visitIdentifier}
          />
        </Box>
      )}
      <BottomBar
        activeStep={0}
        isBackEnabled={false}
        isNextEnabled={Boolean(
          (value.conditionId && value.conditionId !== NO_CONDITION) ||
            (value.conditionId === NO_CONDITION && value.bodySiteId)
        )}
        onNext={handleNext}
        t={t}
        totalSteps={5}
      />
    </Box>
  );
}
